import { useEffect, useState } from 'react'

import { api } from '../../api/client'
import { readState, subscribe, unsubscribe, type PushSchritt, type PushState } from '../../lib/push'

/**
 * Was gerade läuft — in Worten, nicht als Punktereihe.
 *
 * Ein Knopf, der „Einen Moment …" sagt und dann schweigt, ist eine stille
 * Absage: aus dem Betrieb genau so gemeldet (#24, Windows).
 */
const SCHRITTE: Record<PushSchritt, string> = {
  schluessel: 'Schlüssel vom Server holen …',
  erlaubnis: 'Der Browser fragt nach der Erlaubnis …',
  worker: 'Hintergrunddienst anmelden …',
  abonnement: 'Abonnement beim Push-Dienst lösen …',
  server: 'Gerät beim Server eintragen …',
}

/**
 * Wie lange ein Schritt dauern darf, bevor die Seite dazu etwas sagt.
 *
 * Drei Sekunden: kurz genug, dass niemand ratlos davor sitzt, lang genug,
 * dass der gewöhnliche Fall — Frage kommt, Antwort kommt — ohne Zusatztext
 * durchläuft.
 */
const GEDULD = 3000

/**
 * Benachrichtigungen ein- und ausschalten.
 *
 * Gefragt wird erst auf Knopfdruck. Ungefragt darf keine Seite nach der
 * Erlaubnis fragen — und wer einmal abgelehnt hat, kann es im Browser nur
 * mühsam zurücknehmen, deshalb soll die Frage bewusst kommen.
 *
 * Die Karte erscheint nur, wenn der Server Benachrichtigungen überhaupt
 * eingerichtet hat. Ein Schalter, der nichts bewirkt, ist schlimmer als
 * keiner.
 */
export function PushCard() {
  const [server, setServer] = useState<{ enabled: boolean } | null>(null)
  const [state, setState] = useState<PushState | null>(null)
  const [busy, setBusy] = useState(false)
  const [schritt, setSchritt] = useState<PushSchritt | null>(null)
  const [dauert, setDauert] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  // Bleibt ein Schritt stehen, sagt die Seite nach kurzer Zeit, woran es
  // liegen kann. Der Zeitgeber hängt am Schritt: jeder neue setzt ihn zurück.
  useEffect(() => {
    setDauert(false)
    if (schritt === null) return
    const uhr = window.setTimeout(() => setDauert(true), GEDULD)
    return () => window.clearTimeout(uhr)
  }, [schritt])

  useEffect(() => {
    void (async () => {
      try {
        setServer(await api.get<{ enabled: boolean }>('/api/push/key'))
        setState(await readState())
      } catch {
        setServer({ enabled: false })
      }
    })()
  }, [])

  if (!server?.enabled || !state) return null

  async function umschalten() {
    setBusy(true)
    setProblem(null)
    setMeldung(null)
    try {
      if (state?.subscribed) {
        await unsubscribe()
      } else {
        await subscribe(setSchritt)
      }
      setState(await readState())
    } catch (fehler) {
      setProblem(fehler instanceof Error ? fehler.message : 'Das hat nicht geklappt.')
    } finally {
      setBusy(false)
      setSchritt(null)
    }
  }

  async function probe() {
    setBusy(true)
    setProblem(null)
    setMeldung(null)
    try {
      const antwort = await api.post<{ devices: number }>('/api/push/test')
      setMeldung(
        antwort.devices === 1
          ? 'Verschickt — sie sollte gleich ankommen.'
          : `An ${antwort.devices} Geräte verschickt.`,
      )
    } catch (fehler) {
      setProblem(fehler instanceof Error ? fehler.message : 'Das hat nicht geklappt.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card card--seite">
      <h2 className="card__titel">Benachrichtigungen</h2>

      {!state.supported ? (
        <p className="card__lead" style={{ marginBottom: 0 }}>
          {state.needsInstall
            ? 'Auf dem iPhone erst, wenn die App über Teilen → Zum Home-Bildschirm installiert ist. Öffne sie danach von dort.'
            : 'Dieser Browser kann keine Benachrichtigungen empfangen.'}
        </p>
      ) : (
        <>
          <p className="card__lead">
            {state.subscribed
              ? 'Dieses Gerät bekommt eine Nachricht, wenn es etwas Neues gibt. Für einzelne Reisen lässt sich das dort abschalten.'
              : 'Bekomme eine Nachricht, wenn jemand etwas Neues schreibt — auch wenn die App zu ist.'}
          </p>

          {state.stale && (
            <p className="notice" role="alert">
              Die Anmeldung dieses Geräts gehört zu einem älteren Schlüssel des
              Servers — der Push-Dienst weist sie ab. Einmal einschalten
              erneuert sie.
            </p>
          )}

          {/* War schon einmal an und ist es nicht mehr. Das passiert ohne
              Zutun -- iOS räumt bei einer selten benutzten Web-App auf --, und
              man merkt es sonst erst daran, dass nichts mehr ankommt. Die
              Anwendung versucht es beim Start von selbst; hier steht, was
              übrig bleibt, wenn auch die Erlaubnis weg ist. */}
          {state.wanted && !state.subscribed && !state.stale && (
            <p className="notice" role="alert">
              Dieses Gerät war schon einmal angemeldet, ist es aber nicht mehr.
              Einmal einschalten stellt es wieder her.
            </p>
          )}

          {state.permission === 'denied' && !state.subscribed && (
            <p className="notice" role="alert">
              Der Browser hat die Erlaubnis gesperrt. Sie lässt sich in seinen
              Einstellungen für diese Seite wieder erteilen.
            </p>
          )}

          {busy && schritt !== null && (
            <p className="card__lead" aria-live="polite">{SCHRITTE[schritt]}</p>
          )}

          {/* Der eine Schritt, der ohne Zutun nie zu Ende geht. Chrome und
              Edge klappen die Frage am Rechner in ein Symbol in der
              Adresszeile, statt ein Fenster zu stellen -- wer das nicht weiß,
              wartet vergeblich. */}
          {busy && schritt === 'erlaubnis' && dauert && (
            <p className="notice" role="alert">
              Die Frage steht noch offen. Am Rechner stellt der Browser sie oft
              nicht als Fenster, sondern als kleines Symbol links in der
              Adresszeile — dort antworten, dann geht es weiter.
            </p>
          )}

          {busy && schritt !== null && schritt !== 'erlaubnis' && dauert && (
            <p className="notice" role="alert">
              Das dauert ungewöhnlich lange. Wenn nichts weitergeht, hilft die
              Seite neu zu laden und es noch einmal zu versuchen.
            </p>
          )}

          {problem && <p className="notice" role="alert">{problem}</p>}
          {meldung && (
            <p className="card__lead" style={{ color: 'var(--accent)' }}>{meldung}</p>
          )}

          <button
            className="button button--outline"
            type="button"
            disabled={busy || (state.permission === 'denied' && !state.subscribed)}
            onClick={() => void umschalten()}
          >
            {busy
              ? 'Einen Moment …'
              : state.subscribed
                ? 'Auf diesem Gerät abschalten'
                : 'Auf diesem Gerät einschalten'}
          </button>

          {state.subscribed && (
            <button
              className="button button--outline"
              type="button"
              style={{ marginTop: 10 }}
              disabled={busy}
              onClick={() => void probe()}
            >
              Probe senden
            </button>
          )}
        </>
      )}
    </div>
  )
}
